import { lazy } from 'react';

import CookieConsentServiceInstance from '@services/CookieConsentService';
import { CookieCategory } from '@shared-types/optanon.types';
const CookiePlaceholder = lazy(() => import(/* webpackPrefetch: true */ '@shared/CookiePlaceholder'));

export class TweetBook {
  constructor() {
    if (location.hash.indexOf('#closewindow') > -1) {
      window.close();
    }

    this.init();
  }

  init() {
    const twitterElements = Array.from(document.querySelectorAll<HTMLElement>('.embedded-twitter.embed-loading'));
    TweetBook.initTwitterEmbeds(twitterElements);

    const youtubeElements = Array.from(document.querySelectorAll<HTMLElement>('.embedded-youtube.embed-loading'));
    TweetBook.initYoutubeEmbeds(youtubeElements);

    const instagramElements = Array.from(document.querySelectorAll<HTMLElement>('.embedded-instagram.embed-loading'));
    TweetBook.initInstagramEmbeds(instagramElements);

    const facebookElements = Array.from(document.querySelectorAll<HTMLElement>('.embedded-facebook.embed-loading'));
    TweetBook.initFacebookEmbeds(facebookElements);
  }

  static loadTwitterScript(elements: HTMLElement[]) {
    return CookieConsentServiceInstance.appendCookieScript(
      'https://platform.twitter.com/widgets.js',
      CookieCategory.TARGETING,
      undefined,
      { containers: elements, Placeholder: CookiePlaceholder },
    );
  }

  static async loadFacebookScript(elements: HTMLElement[]) {
    const FB: facebook.FacebookStatic = await CookieConsentServiceInstance.appendCookieScript(
      'https://connect.facebook.net/en_US/sdk.js',
      CookieCategory.TARGETING,
      'FB',
      { containers: elements, Placeholder: CookiePlaceholder },
    );
    FB.init({
      autoLogAppEvents: true,
      xfbml: true,
      version: 'v8.0',
    });
    return FB;
  }

  static async getEmbedMarkup(path: string, url: string, element: HTMLElement): Promise<string> {
    const params = {
      url,
      omitscript: true,
    };

    const FB = await this.loadFacebookScript([element]);

    return new Promise((resolve, reject) => {
      FB.api(path, params, (response: { error?: string; html?: string }) => {
        if (!response || response.error) {
          reject(response?.error);
          return '';
        }
        resolve(response.html);
      });
    });
  }

  static async initTwitterEmbeds(elements: HTMLElement[]) {
    if (!elements?.length) return;

    elements.forEach((e) => {
      if (e.dataset.original) e.innerHTML = e.dataset.original;
    });
    await this.loadTwitterScript(elements);
    const twttrWidgets = window.twttr?.widgets;

    elements.forEach(async (e) => {
      if (!e.classList.contains('embed-loading')) return;

      if (!twttrWidgets) {
        e.classList.add('embed-fallback');
      } else {
        const twitterId = e.dataset.twitterid;

        if (twitterId) {
          twttrWidgets.createTweet(twitterId, e);
        } else {
          await twttrWidgets.load(e);
        }
      }
      e.classList.remove('embed-loading');
    });
  }

  /**
   * Process youtube embeds
   */
  static async initYoutubeEmbeds(elements: HTMLElement[]) {
    if (!elements?.length) return;

    await CookieConsentServiceInstance.waitForCookiesAccepted(CookieCategory.TARGETING, {
      containers: elements,
      Placeholder: CookiePlaceholder,
    });

    elements.forEach((e) => {
      const youtubeId = e.dataset.youtubeid;
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${youtubeId}`;
      e.appendChild(iframe);
      e.classList.remove('embed-loading');
    });
  }

  static loadInstagramScript(elements: HTMLElement[]) {
    return CookieConsentServiceInstance.appendCookieScript(
      'https://www.instagram.com/embed.js',
      CookieCategory.TARGETING,
      'instgrm',
      { containers: elements, Placeholder: CookiePlaceholder },
    );
  }

  /**
   * Process instagram embeds
   */
  static async initInstagramEmbeds(elements: HTMLElement[]) {
    if (!elements?.length) return;

    await Promise.allSettled(
      elements.map(async (e) => {
        const instagramId = e.dataset.instagramid;
        e.innerHTML = await this.getEmbedMarkup('/instagram_oembed', `https://www.instagram.com/p/${instagramId}`, e);
        e.classList.remove('embed-loading');
      }),
    );

    const instgrm = await this.loadInstagramScript(elements);
    instgrm && instgrm.Embeds.process();
  }

  /**
   * Process facebook embeds
   */
  static initFacebookEmbeds(elements: HTMLElement[]) {
    if (!elements?.length) return;

    elements.forEach(async (e) => {
      const facebookUrl = e.dataset.url;
      e.innerHTML = await this.getEmbedMarkup('/oembed_post', facebookUrl, e);
      e.classList.remove('embed-loading');
    });
  }

  attachOpenPopupEvent(
    elements: Element[],
    urlToCall: string,
    url: string,
    w: number,
    h: number,
    callback?: (u: string) => unknown,
  ) {
    const left = screen.width / 2 - w / 2;
    const top = 100;

    const style = `toolbar=0,location=0,directories=0,status=0,scrollbars=0,menubar=0,resizable=1,width=${w},height=${h},top=${top},left=${left}`;

    elements.forEach((e) => {
      e.addEventListener('click', () => {
        window.open(urlToCall, 'share_popup', style);
        if (typeof callback === 'function') callback(url);
      });
    });

    return elements;
  }

  initTweetShareButton(
    elements: Element[],
    url: string,
    text: string,
    via: string,
    hashtags: string,
    lang: string,
    w: number,
    h: number,
    clickCallback?: (u: string) => unknown,
  ) {
    //https://dev.twitter.com/docs/intents
    if (!url) return elements;

    let urlToCall = `https://twitter.com/intent/tweet/?dum=1&url=${encodeURIComponent(url)}`;

    if (via) urlToCall += `&via=${via}`;
    if (hashtags) urlToCall += `&hashtags=${hashtags}`;
    if (lang) urlToCall += `&lang=${lang}`;
    if (text) {
      // shorten text if necessary
      let l = 140 - 24; // standard shortened twitter URL (23 chars by default)
      if (via) l -= 6 + via.length; // " via @" + via
      if (hashtags) l -= 2 + hashtags.length; // " #" + hashtag
      if (text.length > l) text = `${text.substring(0, l - 4)}...`;
      // add text to tweet
      urlToCall += `&text=${encodeURIComponent(text)}`;
    }

    return this.attachOpenPopupEvent(elements, urlToCall, url, w, h, clickCallback);
  }

  initFacebookShareButton(
    elements: Element[],
    url: string,
    w: number,
    h: number,
    clickCallback?: (u: string) => unknown,
  ) {
    // this is the only method for custom buttons (not facebook widget buttons)
    if (!url) return elements;

    const urlToCall = `http://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

    return this.attachOpenPopupEvent(elements, urlToCall, url, w, h, clickCallback);
  }

  initVkShareButton(elements: Element[], url: string, w: number, h: number, clickCallback?: (u: string) => unknown) {
    //https://vk.com/dev/Share
    if (!url) return elements;

    const urlToCall = `http://vk.com/share.php?url=${encodeURIComponent(url)}`;

    return this.attachOpenPopupEvent(elements, urlToCall, url, w, h, clickCallback);
  }

  initFacebookDialogButton(
    elements: Element[],
    url: string,
    quote: string,
    hashtag: string,
    w: number,
    h: number,
    clickCallback?: (u: string) => unknown,
  ) {
    // this is the only method for custom buttons (not facebook widget buttons)
    if (!url) return elements;

    let urlToCall = `https://www.facebook.com/dialog/share?display=popup&href=${encodeURIComponent(url)}
      &redirect_uri=${encodeURIComponent(`${url}#closewindow`)}`;

    if (quote) urlToCall += `&quote=${encodeURIComponent(quote)}`;
    if (hashtag) urlToCall += `&hashtag=${encodeURIComponent(`#${hashtag}`)}`;

    return this.attachOpenPopupEvent(elements, urlToCall, url, w, h, clickCallback);
  }

  initFacebookFeedButton(
    elements: Element[],
    url: string,
    title: string,
    description: string,
    image: string,
    w: number,
    h: number,
    clickCallback?: (u: string) => unknown,
  ) {
    // this is the only method for custom buttons (not facebook widget buttos)
    if (!url) return elements;

    let urlToCall = `https://www.facebook.com/dialog/feed?display=popup&link=${encodeURIComponent(url)}
      &redirect_uri=${encodeURIComponent(`${url}#closewindow`)}`;

    if (title) urlToCall += `&name=${encodeURIComponent(title)}`;
    if (description) urlToCall += `&description=${encodeURIComponent(description)}`;
    if (image) urlToCall += `&picture=${encodeURIComponent(image)}`;

    return this.attachOpenPopupEvent(elements, urlToCall, url, w, h, clickCallback);
  }

  static readonly instance = new TweetBook();
}

export default TweetBook.instance;
